import React, { useCallback, useState } from 'react';
import { Button, ButtonVariants, Text, TextAs, TextSizes } from '@clockout-dev/design-elements';
import EmptyState from '../emptyState/EmptyState';
import PayrollConnectionEventList from './PayrollConnectionEventList';
import PayrollConnectionHistoryModal from './PayrollConnectionHistoryModal';
import { PREVIEW_EVENT_COUNT } from './payrollConnections.constants';
import styles from './styles/payroll-connections.module.scss';

/**
 * The connection record for one payroll connection, as a short preview.
 *
 * The card shows only the newest `PREVIEW_EVENT_COUNT` events. A member who
 * has relinked a job a dozen times produces four events per attempt, and an
 * inline list of eighty pushes every other card off the rail. "View all
 * events" opens the full record in a modal, where the remaining pages arrive
 * as the reader scrolls.
 *
 * Note the preview never triggers a request of its own: the first page is
 * already loaded by the time the card expands, so trimming to five is pure
 * presentation. Only scrolling the modal costs a round trip.
 *
 * Every non-row state is explicit. A silent blank card is the one outcome this
 * must never produce, because "nothing happened" and "we could not tell you
 * what happened" look identical to an agent reading the page.
 *
 * @param {Object} props
 * @param {Array<Object>} [props.items] Events, newest first.
 * @param {{ page: number, count?: number, hasNext: boolean } | null} [props.pageinfo]
 * @param {boolean} [props.loading] First-page load.
 * @param {boolean} [props.loadingMore] A scroll-triggered page in flight.
 * @param {Object | null} [props.error] First-page failure: nothing to show.
 * @param {Object | null} [props.moreError] A later page failed. Kept apart from
 *   `error` on purpose — the rows already loaded are still valid, so this is
 *   reported under them instead of replacing them.
 * @param {() => void} [props.onRetry]
 * @param {() => void} [props.onLoadMore]
 * @param {string} [props.timezone] Declared zone from the payload.
 * @param {string} [props.connectionLabel] Platform name, for the modal title.
 */
function PayrollConnectionHistory({
    items,
    pageinfo,
    loading = false,
    loadingMore = false,
    error = null,
    moreError = null,
    onRetry,
    onLoadMore,
    timezone,
    connectionLabel,
}) {
    const [modalOpen, setModalOpen] = useState(false);
    const openModal = useCallback(() => setModalOpen(true), []);
    const closeModal = useCallback(() => setModalOpen(false), []);

    if (error) {
        return (
            <div className={styles.historyState}>
                <EmptyState
                    title="Couldn't load connection history"
                    description="The connection record could not be read. Please try again."
                />
                {onRetry ? (
                    <Button variant={ButtonVariants.DEFAULT} className={styles.retryButton} onClick={() => onRetry()}>
                        Retry
                    </Button>
                ) : null}
            </div>
        );
    }

    // Only the very first page shows a bare loading state; later pages keep the
    // rows on screen and say so underneath them, inside the modal.
    if (loading && !items) {
        return (
            <div className={styles.historyState}>
                <Text as={TextAs.P} size={TextSizes.SMALL} className={styles.historyNote}>
                    Loading connection history…
                </Text>
            </div>
        );
    }

    if (Array.isArray(items) && items.length === 0) {
        return (
            <div className={styles.historyState}>
                <EmptyState title="No connection events" description="This connection has no recorded events yet." />
            </div>
        );
    }

    if (!Array.isArray(items)) {
        return null;
    }

    // Named for what the click does. It opens the full record; it fetches
    // nothing — everything the preview trims is already in memory, and only
    // scrolling the modal costs a request. "Load more" promised the opposite.
    // Two reasons there can be more to see, and both have to open the modal:
    // events already loaded but trimmed out of the preview, and pages the
    // endpoint still has. Checking only `hasNext` would hide events that are
    // sitting in memory on the last page.
    const hidden = items.length - PREVIEW_EVENT_COUNT;
    const hasMore = hidden > 0 || !!pageinfo?.hasNext;

    return (
        <div className={styles.history}>
            <PayrollConnectionEventList
                items={items.slice(0, PREVIEW_EVENT_COUNT)}
                timezone={timezone}
                ariaLabel="Recent connection events"
            />

            {hasMore ? (
                <Button variant={ButtonVariants.DEFAULT} className={styles.showMoreButton} onClick={openModal}>
                    View all events
                </Button>
            ) : null}

            {/* Mounted only while open so the full list costs nothing to keep
                a collapsed-then-expanded card cheap. */}
            {modalOpen ? (
                <PayrollConnectionHistoryModal
                    open={modalOpen}
                    onClose={closeModal}
                    items={items}
                    pageinfo={pageinfo}
                    loadingMore={loadingMore}
                    moreError={moreError}
                    onLoadMore={onLoadMore}
                    timezone={timezone}
                    connectionLabel={connectionLabel}
                />
            ) : null}
        </div>
    );
}

export default PayrollConnectionHistory;
